import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Check, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEASURES_OPTIONS = [
  { key: "pflegekurs", label: "Pflegekurs/-schulung" },
  { key: "kombinationsleistung", label: "Kombinationsleistung" },
  { key: "verhinderungspflege", label: "Verhinderungspflege" },
  { key: "reha", label: "Rehabilitationsleistungen" },
  { key: "tagesnachtpflege", label: "Tages-/Nachtpflege" },
  { key: "alltagsunterstuetzung", label: "Angebote zur Unterstützung im Alltag" },
  { key: "kurzzeitpflege", label: "Kurzzeitpflege" },
  { key: "hilfsmittel", label: "Pflege-/Hilfsmittel/technische Hilfen" },
  { key: "pflegebegutachtung", label: "Erneute Pflegebegutachtung" },
  { key: "wohnraumanpassung", label: "Wohnraumanpassung" },
  { key: "freistellung", label: "Freistellungsmöglichkeiten" },
  { key: "pflegezeit", label: "Pflegezeit/Familienpflegezeit" },
];

export default function CRMBeratungsbogen() {
  const { user } = useAuth();
  const { id, appointmentId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    appointment_id: "", customer_id: "",
    insurance_number: "", patient_name: "", patient_vorname: "",
    patient_geburtsdatum: "", patient_street: "", patient_plz: "", patient_city: "",
    consultation_date: "", consultation_time_from: "", consultation_time_to: "",
    is_video_conference: true,
    assessment_patient_view: "", assessment_consultant_view: "",
    care_situation_ensured: "", care_situation_no_reason: "",
    measures_suggested: "", measures: [], measures_notes: "",
    needs_7a_consultation: false, informed_about_support: false, wishes_7a_consultation: false,
    consent_section3: false, consent_section4: false, consent_examination: false, consent_7a_processing: false,
    insurance_name: "", insurance_street: "", insurance_plz: "", insurance_city: "",
    consultant_name: "Lehnen & Weinacht", consultant_ik: "",
    status: "draft",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(!id);

  useEffect(() => {
    if (id) {
      loadBogen();
    } else if (appointmentId) {
      loadAppointmentData();
    } else {
      setLoading(false);
    }
  }, [id, appointmentId]);

  const loadBogen = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const res = await axios.get(`${API}/beratungsboegen/${id}`, { headers });
      setForm(res.data);
      setIsNew(false);
    } catch { toast.error("Bogen nicht gefunden"); navigate(-1); }
    setLoading(false);
  };

  const loadAppointmentData = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const res = await axios.get(`${API}/appointments/${appointmentId}`, { headers });
      const apt = res.data;
      // Load customer data
      const custRes = await axios.get(`${API}/customers/${apt.customer_id}`, { headers });
      const cust = custRes.data;
      setForm(prev => ({
        ...prev,
        appointment_id: apt.id,
        customer_id: apt.customer_id,
        patient_name: cust.name?.split(" ").slice(-1)[0] || cust.name || "",
        patient_vorname: cust.name?.split(" ").slice(0, -1).join(" ") || "",
        patient_geburtsdatum: cust.date_of_birth || "",
        patient_street: cust.address_street || "",
        patient_plz: cust.address_plz || "",
        patient_city: cust.address_city || "",
        insurance_number: cust.insurance_number || "",
        consultation_date: apt.date || "",
        consultation_time_from: apt.time_slot || "",
        consultation_time_to: apt.time_slot ? `${parseInt(apt.time_slot) + 1}:00` : "",
      }));
      setIsNew(true);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleSave = async (status = "draft") => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const payload = { ...form, status };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.created_by;

      if (isNew) {
        const res = await axios.post(`${API}/beratungsboegen`, payload, { headers });
        toast.success("Beratungsbogen gespeichert");
        navigate(`/app/beratung/${res.data.id}`, { replace: true });
        setIsNew(false);
      } else {
        await axios.put(`${API}/beratungsboegen/${id || form.id}`, payload, { headers });
        toast.success(status === "completed" ? "Beratungsbogen abgeschlossen" : "Änderungen gespeichert");
      }
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
    setSaving(false);
  };

  const handleExportPDF = async () => {
    const bogenId = id || form.id;
    if (!bogenId) { toast.error("Bitte speichern Sie zuerst den Bogen."); return; }
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const res = await axios.get(`${API}/beratungsboegen/${bogenId}/pdf`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Beratungsbogen_${form.patient_vorname || ""}_${form.patient_name || ""}_${form.consultation_date || ""}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch { toast.error("Fehler beim PDF-Export"); }
  };

  const toggleMeasure = (key) => {
    const current = form.measures || [];
    setForm({ ...form, measures: current.includes(key) ? current.filter(m => m !== key) : [...current, key] });
  };

  const Field = ({ label, field, type = "text", placeholder = "", rows }) => (
    <div>
      {label && <Label className="text-xs md:text-sm font-semibold text-slate-600 mb-1.5 block">{label}</Label>}
      {rows ? (
        <textarea value={form[field] || ""} onChange={(e) => setForm({...form, [field]: e.target.value})} rows={rows} placeholder={placeholder} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base resize-none focus:ring-2 focus:ring-cyan-600 focus:ring-offset-1 focus:outline-none min-h-[44px]" />
      ) : (
        <Input type={type} value={form[field] || ""} onChange={(e) => setForm({...form, [field]: e.target.value})} placeholder={placeholder} className="h-12 rounded-xl text-base" />
      )}
    </div>
  );

  const Checkbox = ({ label, field }) => (
    <label className="flex items-start gap-3 cursor-pointer group py-1.5 active:bg-slate-50 rounded-lg -mx-1 px-1">
      <input type="checkbox" checked={form[field] || false} onChange={(e) => setForm({...form, [field]: e.target.checked})} className="mt-0.5 h-6 w-6 rounded border-slate-300 text-cyan-700 focus:ring-cyan-600" />
      <span className="text-sm md:text-base text-slate-700 leading-relaxed group-hover:text-slate-900">{label}</span>
    </label>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-cyan-700 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="crm-beratungsbogen" className="max-w-4xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-cyan-700 mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Zurück
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-['Manrope'] text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-cyan-700" />
            Beratungsbogen §37 Abs. 3 SGB XI
          </h1>
          <p className="text-sm text-slate-500 mt-1">{isNew ? "Neuer Bogen" : `Status: ${form.status === "draft" ? "Entwurf" : "Abgeschlossen"}`}</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {!isNew && (
            <Button variant="outline" className="rounded-xl flex-1 sm:flex-none h-11 active:scale-95" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
          )}
          <Button variant="outline" className="rounded-xl flex-1 sm:flex-none h-11 active:scale-95" onClick={() => handleSave("draft")} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Speichern</>}
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white rounded-xl flex-1 sm:flex-none h-11 active:scale-95" onClick={() => handleSave("completed")} disabled={saving}>
            <Check className="h-4 w-4 mr-1" /> Abschließen
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Section: Patient data */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800 mb-4">Angaben zur pflegebedürftigen Person</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Versichertennummer" field="insurance_number" placeholder="Pflegeversichertennr." />
            <Field label="Name" field="patient_name" placeholder="Nachname" />
            <Field label="Vorname" field="patient_vorname" placeholder="Vorname" />
            <Field label="Geburtsdatum" field="patient_geburtsdatum" type="date" />
            <Field label="Straße" field="patient_street" placeholder="Straße und Hausnr." />
            <Field label="PLZ" field="patient_plz" placeholder="PLZ" />
            <Field label="Ort" field="patient_city" placeholder="Ort" />
          </div>
        </div>

        {/* Section: Consultation details */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800 mb-4">Beratungsbesuch</h2>
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <Field label="Datum" field="consultation_date" type="date" />
            <Field label="Von (Uhr)" field="consultation_time_from" placeholder="10:00" />
            <Field label="Bis (Uhr)" field="consultation_time_to" placeholder="11:00" />
          </div>
          <Checkbox label="Die Beratung erfolgte im Rahmen einer Videokonferenz" field="is_video_conference" />
        </div>

        {/* Section 1: Patient assessment */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800 mb-2">1. Einschätzung aus Sicht der pflegebedürftigen Person / Pflegeperson</h2>
          <p className="text-xs text-slate-500 mb-3">Wie wird die aktuelle Pflege- und Betreuungssituation eingeschätzt?</p>
          <Field label="" field="assessment_patient_view" rows={5} placeholder="Beschreibung der Situation aus Sicht des Pflegebedürftigen und der Pflegeperson..." />
        </div>

        {/* Section 2: Consultant assessment */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800 mb-2">2. Einschätzung aus Sicht der Beratungsperson</h2>
          <Field label="" field="assessment_consultant_view" rows={5} placeholder="Fachliche Einschätzung der Pflege- und Betreuungssituation..." />
        </div>

        {/* Section 3: Care situation ensured */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800 mb-4">3. Ist die Pflege- und Betreuungssituation sichergestellt?</h2>
          <div className="flex gap-4 mb-3">
            {["yes", "no"].map((v) => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="care_ensured" checked={form.care_situation_ensured === v} onChange={() => setForm({...form, care_situation_ensured: v})} className="h-4 w-4 text-cyan-700 focus:ring-cyan-600" />
                <span className="text-sm font-medium text-slate-700">{v === "yes" ? "Ja" : "Nein, weil:"}</span>
              </label>
            ))}
          </div>
          {form.care_situation_ensured === "no" && (
            <Field label="" field="care_situation_no_reason" rows={3} placeholder="Begründung..." />
          )}
        </div>

        {/* Section 4: Measures */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800 mb-4">4. Werden Maßnahmen zur Verbesserung angeregt?</h2>
          <div className="flex gap-4 mb-4">
            {["no", "yes"].map((v) => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="measures" checked={form.measures_suggested === v} onChange={() => setForm({...form, measures_suggested: v})} className="h-4 w-4 text-cyan-700 focus:ring-cyan-600" />
                <span className="text-sm font-medium text-slate-700">{v === "no" ? "Nein" : "Ja, folgende:"}</span>
              </label>
            ))}
          </div>
          {form.measures_suggested === "yes" && (
            <>
              <div className="grid sm:grid-cols-2 gap-1 mb-4">
                {MEASURES_OPTIONS.map((m) => (
                  <label key={m.key} className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors">
                    <input type="checkbox" checked={(form.measures || []).includes(m.key)} onChange={() => toggleMeasure(m.key)} className="h-6 w-6 rounded border-slate-300 text-cyan-700 focus:ring-cyan-600 shrink-0" />
                    <span className="text-sm md:text-base text-slate-700">{m.label}</span>
                  </label>
                ))}
              </div>
              <Field label="Weitere Maßnahmen und Erläuterungen" field="measures_notes" rows={3} placeholder="Erläuterungen zu den empfohlenen Maßnahmen..." />
            </>
          )}
        </div>

        {/* Section 5 + 6 */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6 space-y-4">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800">5. & 6. Pflegeberatung & Information</h2>
          <Checkbox label="Aus Sicht der Beratungsperson ist eine Pflegeberatung nach §7a SGB XI angezeigt." field="needs_7a_consultation" />
          <Checkbox label="Auf Pflegestützpunkte und Pflegeberatung nach §7a SGB XI wurde hingewiesen." field="informed_about_support" />
          <Checkbox label="Die pflegebedürftige Person wünscht eine Pflegeberatung nach §7a SGB XI." field="wishes_7a_consultation" />
        </div>

        {/* Consent */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6 space-y-4">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800">Einwilligungserklärung</h2>
          <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Hinweis Videokonferenz: Die Einwilligung genügt in mündlicher Form. Die Felder werden nach Absprache durch die Beratungsperson angekreuzt.
          </p>
          <Checkbox label="Einwilligung zur Übermittlung der Angaben zu Ziffer 3 (Sicherstellung) an die Pflegekasse." field="consent_section3" />
          <Checkbox label="Einwilligung zur Übermittlung der Empfehlungen zu Ziffer 4 an die Pflegekasse." field="consent_section4" />
          <Checkbox label="Einwilligung zur Inaugenscheinnahme und Übermittlung dieser Information." field="consent_examination" />
          <Checkbox label="Einwilligung zur Verarbeitung der Ergebnisse zur Pflegeberatung nach §7a SGB XI." field="consent_7a_processing" />
        </div>

        {/* Insurance + Consultant */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <h2 className="font-['Manrope'] text-base font-bold text-slate-800 mb-4">Pflegekasse / Versicherung</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Field label="Name" field="insurance_name" placeholder="Name der Pflegekasse" />
            <Field label="Straße" field="insurance_street" placeholder="Straße" />
            <Field label="PLZ" field="insurance_plz" placeholder="PLZ" />
            <Field label="Ort" field="insurance_city" placeholder="Ort" />
          </div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Beratungsperson</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name / Firma" field="consultant_name" placeholder="Lehnen & Weinacht" />
            <Field label="IK-Nummer" field="consultant_ik" placeholder="IK des Pflegedienstes" />
          </div>
        </div>

        {/* Save buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pb-8">
          <Button variant="outline" className="rounded-xl px-6 h-12 text-base active:scale-95" onClick={() => handleSave("draft")} disabled={saving}>
            Entwurf speichern
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 h-12 text-base active:scale-95" onClick={() => handleSave("completed")} disabled={saving}>
            <Check className="h-4 w-4 mr-1" /> Abschließen & senden
          </Button>
        </div>
      </div>
    </div>
  );
}
